<style>
.dialog {
  overflow: initial
}
</style>
<template>
  <v-dialog v-model="dialog.flag" persistent scrollable width="620px">
    <v-card>
      <DivLoader v-if="dialog.loading" :loading_type="'article, list-item-two-line, list-item-two-line, actions'">

      </DivLoader>
      <div v-else>
        <v-form ref="add_member_form" lazy-validation @submit.prevent="addMember()">
          <v-card-title class="pl-7">
            <span class="headline">{{ dialog.title }}</span>
          </v-card-title>
          <!-- Scrolls on its own rather than relying on v-dialog's `scrollable`.
               That flag styles `.v-dialog--scrollable > .v-card > .v-card__text`,
               and this card-text sits two elements deeper (inside the loader's
               v-else div and the form), so the rule never reaches it. Without
               the overflow the card grew past the viewport once a screen with
               fifteen filters was picked, and Save/Cancel came to rest on top
               of the filter list. -->
          <v-card-text style="max-height: 65vh; overflow-y: auto">

            <v-container>
              <v-row>
                <v-col class="py-1" cols="12" md="12" sm="12">
                  <v-text-field
                      v-model="form.name"
                      :label="$lang.NAME"
                      :rules="$rules.NAME"
                  ></v-text-field>
                </v-col>
                <v-col
                    class="py-1"
                    cols="12"
                    md="12"
                    sm="12"
                >
                  <v-text-field
                      v-model="form.mobile"
                      :label="$lang.MOBILE"
                      :rules="$rules.MOBILE"
                      maxlength="10"
                  ></v-text-field>
                </v-col>
                <v-col
                    class="py-1"
                    cols="12"
                    md="12"
                    sm="12"
                >
                  <v-text-field
                      v-model="form.email"
                      :label="$lang.EMAIL"
                      :rules="$rules.EMAIL"

                  ></v-text-field>
                </v-col>
                <v-col
                    class="py-1"
                    cols="12"
                    md="12"
                    sm="12"
                >
                  <!-- The ten screens the panel has, by their sidebar labels.
                       The ids are still TabPermission ids, and the permissions
                       this picker does not manage are preserved server-side. -->
                  <v-select
                      v-model="form.user_permissions"
                      :items="permission_list"
                      :label="$lang.PERMISSIONS"
                      chips
                      item-text="name"
                      item-value="id"
                      multiple
                  ></v-select>
                </v-col>

                <!-- Filtered view ------------------------------------------
                     Confines this member to a slice of a screen. Everything
                     configured here is enforced server-side; the member never
                     sees the filter and cannot clear it. -->
                <v-col class="pt-4 pb-1" cols="12">
                  <div class="d-flex align-center">
                    <span class="subtitle-2">Filtered view</span>
                    <v-spacer></v-spacer>
                    <v-btn
                        :disabled="!availableScreens.length"
                        color="secondary_2"
                        outlined
                        rounded
                        small
                        @click="addViewFilter()"
                    >
                      + Add
                    </v-btn>
                  </div>
                  <div class="caption grey--text mt-1">
                    Limits what this member sees on a screen. They cannot see or
                    change it.
                  </div>
                </v-col>

                <v-col v-if="!view_filter_rows.length" class="py-1" cols="12">
                  <div class="caption grey--text">No filtered view. This member
                    sees everything on the screens above.</div>
                </v-col>

                <v-col
                    v-for="(row, index) in view_filter_rows"
                    :key="'vf-' + index"
                    class="py-2"
                    cols="12"
                >
                  <v-card class="pa-3" outlined>
                    <div class="d-flex align-center">
                      <v-select
                          v-model="row.screen"
                          :items="screenItems(row)"
                          dense
                          hide-details
                          item-text="label"
                          item-value="key"
                          label="Screen"
                          @change="onScreenChange(row)"
                      ></v-select>
                      <v-btn class="ml-2" icon small @click="removeViewFilter(index)">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <!-- Screens whose rows are built in the browser from demo
                         data. The restriction is applied where those rows are
                         produced, so it is not tamper-proof the way the
                         server-side ones are. Say so rather than imply
                         otherwise. -->
                    <div
                        v-if="isDemoScreen(row.screen)"
                        class="caption orange--text mt-2 mb-1"
                    >
                      Demo-data screen — this filter is applied in the browser,
                      not on the server.
                    </div>

                    <div
                        v-if="row.screen && !screenFilters(row.screen).length"
                        class="caption grey--text mt-2"
                    >
                      This screen has no filters.
                    </div>

                    <!-- One control per filter, chosen by its kind. The
                         screens do not all filter the same way: a list of
                         cities is not a date range is not a numeric band, and
                         a lock has to be expressible in whatever shape its own
                         screen uses. -->
                    <div
                        v-for="filter in screenFilters(row.screen)"
                        :key="row.screen + '-' + filter.param"
                        class="mt-4"
                    >
                      <!-- Dates: fixed from/to, so a restriction set today
                           still means the same window next month. -->
                      <DateField
                          v-if="filter.kind === 'date_range'"
                          v-model="row.values[filter.param]"
                          :label="filter.label"
                          range
                      />
                      <DateField
                          v-else-if="filter.kind === 'date'"
                          v-model="row.values[filter.param]"
                          :label="filter.label"
                      />

                      <!-- Numeric bands are picked by name and stored as the
                           edges the server filters on, so no second lookup is
                           needed when the restriction is applied. -->
                      <v-select
                          v-else-if="filter.kind === 'band'"
                          :items="filter.options"
                          :label="filter.label"
                          :value="bandValue(row, filter)"
                          clearable
                          dense
                          hide-details
                          item-text="label"
                          item-value="value"
                          @change="setBand(row, filter, $event)"
                      ></v-select>

                      <!-- One value: the server compares these with =, not IN. -->
                      <v-select
                          v-else-if="filter.kind === 'single' || filter.kind === 'bool'"
                          v-model="row.values[filter.param]"
                          :disabled="!dependencyMet(row, filter)"
                          :hint="dependencyHint(row, filter)"
                          :items="filter.options"
                          :label="filter.label"
                          :persistent-hint="!dependencyMet(row, filter)"
                          clearable
                          dense
                          item-text="label"
                          item-value="value"
                      ></v-select>

                      <!-- Everything else takes a list of values. -->
                      <v-select
                          v-else
                          v-model="row.values[filter.param]"
                          :items="filter.options"
                          :label="filter.label"
                          chips
                          clearable
                          deletable-chips
                          dense
                          hide-details
                          item-text="label"
                          item-value="value"
                          multiple
                          small-chips
                      ></v-select>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions class="pb-5 px-5">
            <v-spacer></v-spacer>
            <v-btn class="px-5  py-4 " color="secondary_2" outlined rounded @click="dialog.flag=false">
              <b> {{ $lang.CANCEL }}</b>
            </v-btn>
            <v-btn class="px-7  py-5 " color="secondary_2" rounded type="submit" @click="addMember">
              <b style="color: #ffff"> {{ $lang.SAVE }}</b>
            </v-btn>
          </v-card-actions>
        </v-form>
      </div>
    </v-card>

  </v-dialog>
</template>
<script>
export default {
  props: ['dialog'],
  components: {
    DivLoader: () => import("@/components/ui/DivLoader"),
    DateField: () => import("@/components/karrofin/DateField.vue"),
  },
  data: () => ({
    id: "",
    permission_list: [],
    /* The screen catalogue, straight from the server: every screen, its
       lockable filters, and each filter's selectable values. Server-built
       because the restriction is applied to real rows -- offering a value no
       row carries would configure a member into an empty screen. */
    screens: [],
    /* One row per configured screen. `values` is {param: [value, ...]}. */
    view_filter_rows: [],
    form: {
      name: '',
      email: '',
      mobile: "",
      user_permissions: [],
    },
  }),
  computed: {
    /* A screen can only be filtered if this member can open it -- and only
       once, so a screen already configured drops out of the picker. */
    grantedScreens() {
      const granted = this.form.user_permissions || [];
      const byId = {};
      (this.permission_list || []).forEach((p) => { byId[p.id] = p.screen; });
      const keys = granted.map((id) => byId[id]).filter(Boolean);
      return this.screens.filter((s) => keys.indexOf(s.key) !== -1);
    },
    availableScreens() {
      const taken = this.view_filter_rows.map((r) => r.screen);
      return this.grantedScreens.filter((s) => taken.indexOf(s.key) === -1);
    },
  },
  created() {
    // this.initialize()
  },

  methods: {
    /* function to fetch data from api */
    getMemberDetails(id) {
      const self = this;
      self.id = id
      self.loading = true;
      let params = {
        "id": id
      };
      const successHandler = (response) => {
        if (response.data.success) {
          if (id)
            self.form = response.data.user_data
          self.form.user_permissions = response.data.user_permissions
          self.permission_list = response.data.permission_list
          self.screens = response.data.view_filter_screens || []
          /* Stored shape is {screen: {param: [values]}}; the form wants a list
             of rows so they can be added and removed one at a time. */
          const saved = response.data.view_filters || {};
          self.view_filter_rows = Object.keys(saved).map((screen) => ({
            screen,
            values: Object.assign({}, saved[screen]),
          }));
        }
      };
      const finallyHandler = () => {
        self.table_loading = false;
      };
      self.request_GET(self, self.$urls.TEAMMATE_DETAILS, params, successHandler, null,
          null, finallyHandler);
    },
    /* reset the form */
    resetForm() {
      this.$refs.add_member_form.reset()
      this.view_filter_rows = []
    },
    screenFor(key) {
      return this.screens.find((s) => s.key === key) || null;
    },
    screenFilters(key) {
      const screen = this.screenFor(key);
      return screen ? screen.filters : [];
    },
    isDemoScreen(key) {
      const screen = this.screenFor(key);
      return !!(screen && screen.is_demo_data);
    },
    /* The row's own screen has to stay in its list or the select would show a
       blank once it is chosen. */
    screenItems(row) {
      const mine = this.screenFor(row.screen);
      return mine ? [mine].concat(this.availableScreens) : this.availableScreens;
    },
    /* Bands are stored as the edges the server filters on ({min, max}, or
       {no_score} for "no credit score at all"), not as the band's name -- so
       applying one needs no second lookup. These two map between that and the
       name the dropdown shows. */
    bandValue(row, filter) {
      const stored = row.values[filter.param];
      if (!stored) return null;
      const match = (filter.options || []).find(
        (o) =>
          (!!o.no_score === !!stored.no_score) &&
          o.min === stored.min &&
          o.max === stored.max
      );
      return match ? match.value : null;
    },
    setBand(row, filter, value) {
      if (!value) {
        this.$delete(row.values, filter.param);
        return;
      }
      const band = (filter.options || []).find((o) => o.value === value);
      if (!band) return;
      const stored = {};
      if (band.min !== undefined) stored.min = band.min;
      if (band.max !== undefined) stored.max = band.max;
      if (band.no_score) stored.no_score = true;
      this.$set(row.values, filter.param, stored);
    },
    /* Document status is only honoured alongside a document type -- the server
       picks which column to compare from the type. Saved on its own it would
       be a restriction that does nothing, so the control stays disabled until
       its partner is set, and the server drops it anyway if one gets through. */
    dependencyMet(row, filter) {
      if (!filter.requires) return true;
      const partner = row.values[filter.requires];
      return !!(partner && partner.length !== 0);
    },
    dependencyHint(row, filter) {
      if (this.dependencyMet(row, filter)) return "";
      const partner = this.screenFilters(row.screen)
        .find((f) => f.param === filter.requires);
      return "Pick a " + ((partner && partner.label) || filter.requires) + " first";
    },
    addViewFilter() {
      this.view_filter_rows.push({ screen: null, values: {} });
    },
    removeViewFilter(index) {
      this.view_filter_rows.splice(index, 1);
    },
    /* Values belong to the screen they were picked on. */
    onScreenChange(row) {
      row.values = {};
    },
    /* {screen: {param: [values]}}, dropping rows with nothing chosen so
       "configured but blank" -- which would restrict nothing -- cannot be
       saved by accident. */
    viewFiltersPayload() {
      const out = {};
      this.view_filter_rows.forEach((row) => {
        if (!row.screen) return;
        const values = {};
        Object.keys(row.values || {}).forEach((param) => {
          const value = row.values[param];
          if (value === null || value === undefined || value === "") return;
          // A list must have something in it; a band or a date range is an
          // object and is kept as it is. The server re-checks all of this and
          // drops anything that would restrict nothing.
          if (Array.isArray(value) && !value.length) return;
          values[param] = value;
        });
        if (Object.keys(values).length) out[row.screen] = values;
      });
      return out;
    },
    /* function for save the member data */
    addMember() {
      const self = this;
      if (this.$refs.add_member_form.validate() == false)
        return false
      self.login_loading = true
      var form = new FormData();
      form.append("mobile", self.form.mobile);
      form.append("email", self.form.email);
      form.append("name", self.form.name);
      form.append("user_permissions", JSON.stringify(self.form.user_permissions));
      form.append("view_filters", JSON.stringify(self.viewFiltersPayload()));

      if (self.dialog.is_edit)
        form.append("id", self.id);


      const successHandler = (response) => {
        if (response.data.success) {
          if (self.dialog.is_edit)
            self.showSnakeBar("success", "Successfully updated")
          else
            self.showSnakeBar("success", "Successfully register")

          self.dialog.flag = false
          self.$emit("update")
        }
      };
      const finallyHandler = () => {
        self.login_loading = false
      };
      this.request_POST(self, this.$urls.REGISTER_TEAMMATE, form, successHandler, null, null, finallyHandler)
    },
  },
}
</script>
